import React, { useCallback, useEffect, useState } from "react";
import Header from "../components/Header";
import axiosInstance from "../utils/axiosInstance";
import Session from "../utils/sessionStorage";
import { CiBarcode } from "react-icons/ci";
import { FiRefreshCw } from "react-icons/fi";
import ScannerModal from "../components/ScannerModal";
import ErrorModal from "../components/ErrorModal";
import SuccessModal from "../components/SuccessModal";
import { countries } from "../constants";
import Input from "../components/form/Input";
import { formatDateToYYYYMMDD } from "../utils";

const SelfRegister = () => {
    const token = Session.get("token");
    const profile = Session.get("_user_");
    const userData = profile ? JSON.parse(profile) : {};
    const walletUserSess = Session.get("walletUserSess");
    const [states, setStates] = useState([]);
    const [availableNos, setAvailableNos] = useState([]);
    const [sameAddress, setSameAddress] = useState(false);
    const [loading, setLoading] = useState(false);
    const [numbersLoading, setNumbersLoading] = useState(false);
    const [isMsisdnDropdownOpen, setIsMsisdnDropdownOpen] = useState(false);
    const [openModal, setOpenModal] = useState(false);
    const [errorModal, setErrorModal] = useState({ open: false, message: "" });
    const [successModal, setSuccessModal] = useState({ open: false, message: "" });

    const [form, setForm] = useState(() => {
        const identityType = userData.identityType === "NRIC" ? "MYKAD" : userData.identityType || "";
        const selectedIdentityType = ["MYKAD", "PASSPORT"].includes(identityType) ? identityType : "";
        return {
            full_name: userData.fullName || "",
            first_name: userData.firstName || "",
            gender: userData.gender || "",
            email: userData.email || "",
            date_of_birth: userData.dob || "",
            identity_type: selectedIdentityType,
            identity_no: userData.identityNo || "",
            address1: userData.address || "",
            address2: userData.address2 || "",
            city: userData.city || "",
            state: userData.state || "",
            postal_code: userData.postcode || "",
            msisdn: "",
            iccid: "",
            foreigner_type: "",
            alt_mobile_no: "",
            issuance_country: selectedIdentityType === "MYKAD" ? "MY" : userData.issuance_country || "",
            stay_address_1: "",
            stay_address_2: "",
            stay_city: "",
            stay_postal_code: "",
            stay_state: "",
            ugu_ref_code: "",
            institute_name: "",
            institute_address_1: "",
            institute_address_2: "",
            institute_city: "",
            institute_postal_code: "",
            institute_state: "",
            institute_ref_no: "",
            employer_name: "",
            employer_address_1: "",
            employer_address_2: "",
            employer_city: "",
            employer_postal_code: "",
            employer_state: "",
            emp_ref_no: "",
            hotel_name: "",
        };
    });
    const isPassport = form.identity_type === "PASSPORT";
    // Self registration has no scanned SDK fields to lock.
    const isLocked = () => false;

    const getAvailableNos = useCallback(async (clearSelectedNumber = false) => {
        setNumbersLoading(true);
        try {
            const response = await axiosInstance.get("api/v1/available-numbers", {
                headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
            });
            if (response.status === 200) {
                setAvailableNos(response.data.available_numbers || []);
                if (clearSelectedNumber) setForm((prev) => ({ ...prev, msisdn: "" }));
            }
        } catch (error) {
            setErrorModal({ open: true, message: error?.response?.data?.message || "Failed to load available numbers" });
        } finally {
            setNumbersLoading(false);
        }
    }, [token]);

    useEffect(() => {
        async function getStates() {
            try {
                const response = await axiosInstance.get("api/v1/get-states", {
                    headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
                });
                if (response.status === 200) setStates(response.data.states || []);
            } catch (error) {
                setErrorModal({ open: true, message: error?.response?.data?.message || "Failed to load states" });
            }
        }
        getStates();
        getAvailableNos();
    }, [token, getAvailableNos]);

    useEffect(() => {
        if (sameAddress && isPassport) {
            setForm((prev) => ({
                ...prev,
                address1: prev.stay_address_1,
                address2: prev.stay_address_2,
                city: prev.stay_city,
                state: prev.stay_state,
                postal_code: prev.stay_postal_code,
            }));
        }
    }, [sameAddress, isPassport, form.stay_address_1, form.stay_address_2, form.stay_city, form.stay_state, form.stay_postal_code]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setForm((prev) => ({
            ...prev,
            [name]: value,
            ...(name === "identity_type" ? { issuance_country: value === "MYKAD" ? "MY" : "" } : {}),
        }));
        if (name === "msisdn") setIsMsisdnDropdownOpen(true);
    };

    const handleMsisdnSelect = (msisdn) => {
        setForm((prev) => ({ ...prev, msisdn }));
        setIsMsisdnDropdownOpen(false);
    };

    const validateForm = () => {
        if (!form.full_name) return "Full Name is required";
        if (!form.gender) return "Gender is required";
        if (!form.email) return "Email is required";
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) return "Please enter a valid email address";
        if (!form.date_of_birth) return "Date of Birth is required";
        if (!form.identity_type) return "Identity Type is required";
        if (!form.identity_no) return "Identity Number is required";
        if (!form.address1) return "Address Line 1 is required";
        if (!form.city) return "City is required";
        if (!form.state) return "State is required";
        if (!form.postal_code) return "Postcode is required";
        if (!form.iccid) return "ICCID is required";
        if (!/^\d+$/.test(form.iccid)) return "ICCID should contain only digits";
        if (!form.msisdn) return "Mobile Number is required";
        if (!/^\d+$/.test(form.msisdn)) return "Mobile Number should contain only digits";
        if (!availableNos.some((number) => String(number.MSISDN) === String(form.msisdn))) {
            return "Please select a mobile number from the loaded list";
        }
        if (isPassport) {
            if (!form.foreigner_type) return "Foreigner type is required";
            if (!form.issuance_country) return "Issuance country is required";
            if (!form.stay_address_1) return "Staying address is required";
            if (!form.stay_city) return "Staying city is required";
            if (!form.stay_state) return "Staying state is required";
            if (!form.stay_postal_code) return "Staying postcode is required";
            if (form.alt_mobile_no && !/^\d+$/.test(form.alt_mobile_no)) return "Alternate mobile should contain only digits";
            if (form.foreigner_type === "Student") {
                if (!form.institute_name) return "Institute name is required";
                if (!form.institute_address_1) return "Institute address is required";
                if (!form.institute_city) return "Institute city is required";
                if (!form.institute_postal_code) return "Institute postcode is required";
                if (!form.institute_state) return "Institute state is required";
            } else if (form.foreigner_type === "Worker") {
                if (!form.employer_name) return "Employer name is required";
                if (!form.employer_address_1) return "Employer address is required";
                if (!form.employer_city) return "Employer city is required";
                if (!form.employer_postal_code) return "Employer postcode is required";
                if (!form.employer_state) return "Employer state is required";
            } else if (form.foreigner_type === "Tourist") {
                if (!form.hotel_name) return "Hotel name is required";
            }
        }
        return null;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const validationError = validateForm();
        if (validationError) {
            setErrorModal({ open: true, message: validationError });
            return;
        }
        setLoading(true);
        try {
            const body = {
                msisdn: form.msisdn,
                name: form.full_name,
                first_name: form.first_name,
                gender: form.gender,
                date_of_birth: formatDateToYYYYMMDD(form.date_of_birth),
                address: form.address1,
                address2: form.address2,
                state: form.state,
                city: form.city,
                postal_code: form.postal_code,
                email: form.email,
                plan_info: "JS",
                id_type: form.identity_type === "MYKAD" ? "NRIC" : form.identity_type,
                subid: form.identity_no,
                iccid: form.iccid,
                issuance_country: form.issuance_country,
                alt_mobile_no: form.alt_mobile_no,
                stay_address_1: form.stay_address_1,
                stay_address_2: form.stay_address_2,
                stay_city: form.stay_city,
                stay_state: form.stay_state,
                stay_postal_code: form.stay_postal_code,
                ugu_ref_code: form.ugu_ref_code,
                walletUserSessToken: walletUserSess,
            };
            if (isPassport) {
                body.foreigner_type = form.foreigner_type;
                if (form.foreigner_type === "Student") {
                    Object.assign(body, {
                        institute_name: form.institute_name,
                        institute_address_1: form.institute_address_1,
                        institute_address_2: form.institute_address_2,
                        institute_city: form.institute_city,
                        institute_state: form.institute_state,
                        institute_postal_code: form.institute_postal_code,
                        institute_ref_no: form.institute_ref_no,
                    });
                } else if (form.foreigner_type === "Worker") {
                    Object.assign(body, {
                        employer_name: form.employer_name,
                        employer_address_1: form.employer_address_1,
                        employer_address_2: form.employer_address_2,
                        employer_city: form.employer_city,
                        employer_state: form.employer_state,
                        employer_postal_code: form.employer_postal_code,
                        employer_ref_no: form.emp_ref_no,
                    });
                } else if (form.foreigner_type === "Tourist") {
                    body.hotel_name = form.hotel_name;
                }
            }
            const response = await axiosInstance.post("api/v1/create-profile", body, {
                headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
            });
            if (response.status === 200) {
                setSuccessModal({ open: true, message: response?.data?.message || "Registration successful!" });
            }
        } catch (error) {
            setErrorModal({ open: true, message: error?.response?.data?.message || "Registration failed. Please try again." });
        } finally {
            setLoading(false);
        }
    };

    const handleOpenBarCodeModal = () => setOpenModal(true);
    const closeModal = () => setOpenModal(false);
    const getCode = (code) => {
        if (code) setForm((prev) => ({ ...prev, iccid: code }));
    };
    const filteredAvailableNos = availableNos.filter((number) =>
        String(number.MSISDN).includes(form.msisdn.trim())
    );

    return (
        <>
            <Header />
            <div className="max-w-md my-6 px-4 mx-auto bg-white rounded-lg ">
                <form onSubmit={handleSubmit} className="space-y-4">
                    {/* Full Name */}
                    <div>
                        <label className="block text-[13px] font-medium text-gray-700">Full Name <span className="text-red-500">*</span></label>
                        <input
                            autoComplete="off"
                            type="text"
                            name="full_name"
                            disabled={isLocked("full_name")}
                            value={form.full_name}
                            onChange={handleChange}
                            className="mt-1 block w-full min-h-[50px] text-[#1111117A] border-[1px] border-[#11111126] rounded-[8px] shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                        />
                    </div>

                    {/* First Name */}
                    <div>
                        <label className="block text-[13px] font-medium text-gray-700">First Name (optional)</label>
                        <input
                            autoComplete="off"
                            type="text"
                            name="first_name"
                            disabled={isLocked("first_name")}
                            value={form.first_name}
                            onChange={handleChange}
                            className="mt-1 block w-full min-h-[50px] text-[#1111117A] border-[1px] border-[#11111126] rounded-[8px] shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                        />
                    </div>

                    <div>
                        <label className="block text-[13px] font-medium text-gray-700">Gender <span className="text-red-500">*</span></label>
                        <select
                            autoComplete="off"
                            name="gender"
                            disabled={isLocked("gender")}
                            value={form.gender}
                            onChange={handleChange}
                            className="font-poppins w-full p-3  outline-none text-[#1111117A] rounded-[8px] bg-transparent border-[1px] border-[#11111126] focus:ring-blue-500 focus:border-blue-500  placeholder:text-base placeholder:font-normal text-accent-silver-900 placeholder:text-accent-silver-900"
                        >
                            <option value="">Select Gender</option>
                            <option value="Male">Male</option>
                            <option value="Female">Female</option>
                        </select>
                    </div>

                    {/* Email */}
                    <div>
                        <label className="block text-[13px] font-medium text-gray-700">Email <span className="text-red-500">*</span></label>
                        <input
                            autoComplete="off"
                            type="email"
                            name="email"
                            value={form.email}
                            onChange={handleChange}
                            className="mt-1 block w-full min-h-[50px] text-[#1111117A] border-[1px] border-[#11111126] rounded-[8px] shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                        />
                    </div>

                    {/* Date of Birth */}
                    <div>
                        <label className="block text-[13px] font-medium text-gray-700">Date of Birth <span className="text-red-500">*</span></label>

                        <Input
                            autoComplete="off"
                            type={"date"}
                            name={"date_of_birth"}
                            isDisabled={isLocked("date_of_birth")}
                            intent={"secondary"}
                            size={"lg"}
                            placeholder={"dob"}
                            roundness={"round-sm"}
                            classes={"!block !p-0 box-border w-full min-w-0 max-w-full min-h-[50px] text-[#1111117A] border-[1px] border-[#11111126] rounded-[8px]"}
                            value={form?.date_of_birth}
                            selector={"dob"}
                            eventAction={handleChange}
                        />
                    </div>

                    {/* Identity Type */}
                    <div>
                        <label className="block text-[13px] font-medium text-gray-700">Identity Type <span className="text-red-500">*</span></label>
                        <select
                            autoComplete="off"
                            name="identity_type"
                            disabled={isLocked("identity_type")}
                            value={form.identity_type}
                            onChange={handleChange}
                            className="font-poppins w-full p-3  outline-none text-[#1111117A] rounded-[8px] bg-transparent border-[1px] border-[#11111126] focus:ring-blue-500 focus:border-blue-500  placeholder:text-base placeholder:font-normal text-accent-silver-900 placeholder:text-accent-silver-900"
                        >
                            <option value="">Select Identity Type</option>
                            <option value="MYKAD">MYKAD</option>
                            {/* <option value="PASSPORT">Passport</option> */}
                        </select>
                    </div>

                    {/* Identity Number */}
                    <div>
                        <label className="block text-[13px] font-medium text-gray-700">Identity Number <span className="text-red-500">*</span></label>
                        <input
                            autoComplete="off"
                            type="text"
                            name="identity_no"
                            disabled={isLocked("identity_no")}
                            value={form.identity_no}
                            onChange={handleChange}
                            className="mt-1 block min-h-[50px] w-full text-[#1111117A] border-[1px] border-[#11111126] rounded-[8px] shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                        />
                    </div>

                    {/* Additional fields for passport */}
                    {isPassport && (
                        <>
                            {/* Foreigner Type */}
                            <div>
                                <label className="block text-[13px] font-medium text-gray-700">Foreigner Type <span className="text-red-500">*</span></label>
                                <select
                                    autoComplete="off"
                                    name="foreigner_type"
                                    value={form.foreigner_type}
                                    onChange={handleChange}
                                    className="font-poppins w-full p-3  outline-none text-[#1111117A] bg-transparent border-[1px] border-[#11111126] rounded-[8px] focus:ring-blue-500 focus:border-blue-500  placeholder:text-base placeholder:font-normal text-accent-silver-900 placeholder:text-accent-silver-900"
                                >
                                    <option value="">Select Foreigner Type</option>
                                    <option value="Student">Student</option>
                                    <option value="Worker">Worker</option>
                                    <option value="Tourist">Tourist</option>
                                </select>
                            </div>

                            {/* Alternate Mobile */}
                            <div>
                                <label className="block text-[13px] font-medium text-gray-700">Alternate Mobile</label>
                                <input
                                    autoComplete="off"
                                    type="text"
                                    name="alt_mobile_no"
                                    value={form.alt_mobile_no}
                                    onChange={handleChange}
                                    className="mt-1 block min-h-[50px] w-full text-[#1111117A] border-[1px] border-[#11111126] rounded-[8px] shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                                />
                            </div>

                            {/* Issuance Country */}
                            <div>
                                <label className="block text-[13px] font-medium text-gray-700">Issuance Country <span className="text-red-500">*</span></label>
                                {/* {form.identity_type === "PASSPORT" ? ( */}
                                <select
                                    autoComplete="off"
                                    name="issuance_country"
                                    disabled={isLocked("issuance_country")}
                                    value={form.issuance_country}
                                    onChange={handleChange}
                                    className="font-poppins w-full p-3 rounded-[8px] outline-none text-[#1111117A] bg-transparent border-[1px] border-[#11111126] focus:ring-blue-500 focus:border-blue-500  placeholder:text-base placeholder:font-normal text-accent-silver-900 placeholder:text-accent-silver-900"
                                >
                                    <option value="">Select Issuance Country</option>
                                    {countries.map((country) => (
                                        <option key={country.code} value={country.code}>
                                            {country.name} ({country.code})
                                        </option>
                                    ))}
                                </select>
                                {/* ) : ( */}
                                {/* <input
                                        name="issuance_country"
                                        value="MY"
                                        disabled
                                        className="mt-1 block w-full border-2 border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 bg-gray-100"
                                    />
                                )} */}
                            </div>

                            {/* Staying Address 1 */}
                            <div>
                                <label className="block text-[13px] font-medium text-gray-700">Staying Address Line 1 <span className="text-red-500">*</span></label>
                                <input
                                    autoComplete="off"
                                    type="text"
                                    name="stay_address_1"
                                    value={form.stay_address_1}
                                    onChange={handleChange}
                                    className="mt-1 min-h-[50px] block w-full text-[#1111117A] border-[1px] border-[#11111126] rounded-[8px] shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                                />
                            </div>

                            {/* Staying Address 2 */}
                            <div>
                                <label className="block text-[13px] font-medium text-gray-700">Staying Address Line 2</label>
                                <input
                                    autoComplete="off"
                                    type="text"
                                    name="stay_address_2"
                                    value={form.stay_address_2}
                                    onChange={handleChange}
                                    className="mt-1 min-h-[50px] block w-full text-[#1111117A] border-[1px] border-[#11111126] rounded-[8px] shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                                />
                            </div>

                            {/* Staying City */}
                            <div>
                                <label className="block text-[13px] font-medium text-gray-700">Staying City <span className="text-red-500">*</span></label>
                                <input
                                    autoComplete="off"
                                    type="text"
                                    name="stay_city"
                                    value={form.stay_city}
                                    onChange={handleChange}
                                    className="mt-1 min-h-[50px] block w-full text-[#1111117A] border-[1px] border-[#11111126] rounded-[8px] shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                                />
                            </div>

                            {/* Staying State */}
                            <div>
                                <label className="block text-[13px] font-medium text-gray-700">Staying State <span className="text-red-500">*</span></label>
                                <select
                                    autoComplete="off"
                                    name="stay_state"
                                    value={form.stay_state}
                                    onChange={handleChange}
                                    className="font-poppins w-full p-3 rounded-[8px] outline-none text-base bg-transparent border-[1px] border-[#11111126] focus:ring-blue-500 focus:border-blue-500  placeholder:text-base placeholder:font-normal text-accent-silver-900 placeholder:text-accent-silver-900"
                                >
                                    <option value="">Select State</option>
                                    {states.map((state) => (
                                        <option key={state.code} value={state.code}>
                                            {state.name}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            {/* Staying Postcode */}
                            <div>
                                <label className="block text-[13px] font-medium text-gray-700">Staying Postcode <span className="text-red-500">*</span></label>
                                <input
                                    autoComplete="off"
                                    type="text"
                                    name="stay_postal_code"
                                    value={form.stay_postal_code}
                                    onChange={handleChange}
                                    className="mt-1 min-h-[50px] block w-full text-[#1111117A] border-[1px] border-[#11111126] rounded-[8px] shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                                />
                            </div>

                            <div>
                                <label className="block text-[13px] font-medium text-gray-700">Referral Code</label>
                                <input
                                    autoComplete="off"
                                    type="text"
                                    name="ugu_ref_code"
                                    value={form.ugu_ref_code}
                                    onChange={handleChange}
                                    className="mt-1 min-h-[50px] block w-full text-[#1111117A] border-[1px] border-[#11111126] rounded-[8px] shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                                />
                            </div>

                            {/* Additional fields based on foreigner type */}
                            {form.foreigner_type === "Student" && (
                                <>
                                    <div>
                                        <label className="block text-[13px] font-medium text-gray-700">Institute Name <span className="text-red-500">*</span></label>
                                        <input
                                            autoComplete="off"
                                            type="text"
                                            name="institute_name"
                                            value={form.institute_name}
                                            onChange={handleChange}
                                            className="mt-1 min-h-[50px] block w-full text-[#1111117A] border-[1px] border-[#11111126] rounded-[8px] shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-[13px] font-medium text-gray-700">Institute Address Line 1 <span className="text-red-500">*</span></label>
                                        <input
                                            autoComplete="off"
                                            type="text"
                                            name="institute_address_1"
                                            value={form.institute_address_1}
                                            onChange={handleChange}
                                            className="mt-1 min-h-[50px] block w-full text-[#1111117A] border-[1px] border-[#11111126] rounded-[8px] shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-[13px] font-medium text-gray-700">Institute Address Line 2</label>
                                        <input
                                            autoComplete="off"
                                            type="text"
                                            name="institute_address_2"
                                            value={form.institute_address_2}
                                            onChange={handleChange}
                                            className="mt-1 min-h-[50px] block w-full text-[#1111117A] border-[1px] border-[#11111126] rounded-[8px] shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-[13px] font-medium text-gray-700">Institute City <span className="text-red-500">*</span></label>
                                        <input
                                            autoComplete="off"
                                            type="text"
                                            name="institute_city"
                                            value={form.institute_city}
                                            onChange={handleChange}
                                            className="mt-1 min-h-[50px] block w-full text-[#1111117A] border-[1px] border-[#11111126] rounded-[8px] shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-[13px] font-medium text-gray-700">Institute Postcode <span className="text-red-500">*</span></label>
                                        <input
                                            autoComplete="off"
                                            type="text"
                                            name="institute_postal_code"
                                            value={form.institute_postal_code}
                                            onChange={handleChange}
                                            className="mt-1 min-h-[50px] block w-full text-[#1111117A] border-[1px] border-[#11111126] rounded-[8px] shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-[13px] font-medium text-gray-700">Institute State <span className="text-red-500">*</span></label>
                                        <select
                                            autoComplete="off"
                                            name="institute_state"
                                            value={form.institute_state}
                                            onChange={handleChange}
                                            className="font-poppins w-full p-3 outline-none text-[#1111117A] bg-transparent border-[1px] border-[#11111126] rounded-[8px] focus:ring-blue-500 focus:border-blue-500  placeholder:text-base placeholder:font-normal text-accent-silver-900 placeholder:text-accent-silver-900"
                                        >
                                            <option value="">Select State</option>
                                            {states.map((state) => (
                                                <option key={state.code} value={state.code}>
                                                    {state.name}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                    <div>
                                        <label className="block text-[13px] font-medium text-gray-700">Institute Reference No</label>
                                        <input
                                            autoComplete="off"
                                            type="text"
                                            name="institute_ref_no"
                                            value={form.institute_ref_no}
                                            onChange={handleChange}
                                            className="mt-1 min-h-[50px] block w-full text-[#1111117A] border-[1px] border-[#11111126] rounded-[8px] shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                                        />
                                    </div>
                                </>
                            )}

                            {form.foreigner_type === "Worker" && (
                                <>
                                    <div>
                                        <label className="block text-[13px] font-medium text-gray-700">Employer Name <span className="text-red-500">*</span></label>
                                        <input
                                            autoComplete="off"
                                            type="text"
                                            name="employer_name"
                                            value={form.employer_name}
                                            onChange={handleChange}
                                            className="mt-1 min-h-[50px] block w-full text-[#1111117A] border-[1px] border-[#11111126] rounded-[8px] shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-[13px] font-medium text-gray-700">Employer Address Line 1 <span className="text-red-500">*</span></label>
                                        <input
                                            autoComplete="off"
                                            type="text"
                                            name="employer_address_1"
                                            value={form.employer_address_1}
                                            onChange={handleChange}
                                            className="mt-1 min-h-[50px] block w-full text-[#1111117A] border-[1px] border-[#11111126] rounded-[8px] shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-[13px] font-medium text-gray-700">Employer Address Line 2</label>
                                        <input
                                            autoComplete="off"
                                            type="text"
                                            name="employer_address_2"
                                            value={form.employer_address_2}
                                            onChange={handleChange}
                                            className="mt-1 min-h-[50px] block w-full text-[#1111117A] border-[1px] border-[#11111126] rounded-[8px] shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-[13px] font-medium text-gray-700">Employer City <span className="text-red-500">*</span></label>
                                        <input
                                            autoComplete="off"
                                            type="text"
                                            name="employer_city"
                                            value={form.employer_city}
                                            onChange={handleChange}
                                            className="mt-1 min-h-[50px] block w-full text-[#1111117A] border-[1px] border-[#11111126] rounded-[8px] shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-[13px] font-medium text-gray-700">Employer Postcode <span className="text-red-500">*</span></label>
                                        <input
                                            autoComplete="off"
                                            type="text"
                                            name="employer_postal_code"
                                            value={form.employer_postal_code}
                                            onChange={handleChange}
                                            className="mt-1 min-h-[50px] block w-full text-[#1111117A] border-[1px] border-[#11111126] rounded-[8px] shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-[13px] font-medium text-gray-700">Employer State <span className="text-red-500">*</span></label>
                                        <select
                                            autoComplete="off"
                                            name="employer_state"
                                            value={form.employer_state}
                                            onChange={handleChange}
                                            className="font-poppins w-full p-3 rounded-[8px] outline-none text-[#1111117A] bg-transparent border-[1px] border-[#11111126] focus:ring-blue-500 focus:border-blue-500  placeholder:text-base placeholder:font-normal text-accent-silver-900 placeholder:text-accent-silver-900"
                                        >
                                            <option value="">Select State</option>
                                            {states.map((state) => (
                                                <option key={state.code} value={state.code}>
                                                    {state.name}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                    <div>
                                        <label className="block text-[13px] font-medium text-gray-700">Employee Reference No</label>
                                        <input
                                            autoComplete="off"
                                            type="text"
                                            name="emp_ref_no"
                                            value={form.emp_ref_no}
                                            onChange={handleChange}
                                            className="mt-1 min-h-[50px] block w-full text-[#1111117A] border-[1px] border-[#11111126] rounded-[8px] shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                                        />
                                    </div>
                                </>
                            )}

                            {form.foreigner_type === "Tourist" && (
                                <div>
                                    <label className="block text-[13px] font-medium text-gray-700">Hotel Name <span className="text-red-500">*</span></label>
                                    <input
                                        autoComplete="off"
                                        type="text"
                                        name="hotel_name"
                                        value={form.hotel_name}
                                        onChange={handleChange}
                                        className="mt-1 min-h-[50px] block w-full text-[#1111117A] border-[1px] border-[#11111126] rounded-[8px] shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                                    />
                                </div>
                            )}

                            <div className="flex items-center mt-2">
                                <input
                                    autoComplete="off"
                                    type="checkbox"
                                    checked={sameAddress}
                                    onChange={(e) => setSameAddress(e.target.checked)}
                                    id="sameAddress"
                                    className="h-4 min-h-[50px] w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                                />
                                <label htmlFor="sameAddress" className="ml-2 text-[13px] font-medium text-gray-700">
                                    Address is same as staying address
                                </label>
                            </div>
                        </>
                    )}

                    {/* Address 1 */}
                    <div>
                        <label className="block text-[13px] font-medium text-gray-700">Address Line 1 <span className="text-red-500">*</span></label>
                        <input
                            autoComplete="off"
                            type="text"
                            name="address1"
                            disabled={isLocked("address1")}
                            value={form.address1}
                            onChange={handleChange}
                            className="mt-1 block min-h-[50px] w-full text-[#1111117A] border-[1px] border-[#11111126] rounded-[8px] shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                        />
                    </div>

                    {/* Address 2 */}
                    <div>
                        <label className="block text-[13px] font-medium text-gray-700">Address Line 2</label>
                        <input
                            autoComplete="off"
                            type="text"
                            name="address2"
                            disabled={isLocked("address2")}
                            value={form.address2}
                            onChange={handleChange}
                            className="mt-1 block min-h-[50px] w-full text-[#1111117A] border-[1px] border-[#11111126] rounded-[8px] shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                        />
                    </div>

                    {/* City */}
                    <div>
                        <label className="block text-[13px] font-medium text-gray-700">City <span className="text-red-500">*</span></label>
                        <input
                            autoComplete="off"
                            type="text"
                            name="city"
                            disabled={isLocked("city")}
                            value={form.city}
                            onChange={handleChange}
                            className="mt-1 block min-h-[50px] w-full text-[#1111117A] border-[1px] border-[#11111126] rounded-[8px] shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                        />
                    </div>

                    {/* State - Now a dropdown */}
                    <div>
                        <label className="block  text-[13px] font-medium text-gray-700">State <span className="text-red-500">*</span></label>
                        <select
                            autoComplete="off"
                            name="state"
                            disabled={isLocked("state")}
                            value={form.state}
                            onChange={handleChange}
                            className="font-poppins w-full p-3 rounded-[8px] outline-none text-[#1111117A] bg-transparent border-[1px] border-[#11111126] focus:ring-blue-500 focus:border-blue-500  placeholder:text-base placeholder:font-normal text-accent-silver-900 placeholder:text-accent-silver-900"
                        >
                            <option value="">Select State</option>
                            {states.map((state) => (
                                <option key={state.code} value={state.code}>
                                    {state.name}
                                </option>
                            ))}
                        </select>
                    </div>

                    {/* Postcode */}
                    <div>
                        <label className="block text-[13px] font-medium text-gray-700">Postcode <span className="text-red-500">*</span></label>
                        <input
                            autoComplete="off"
                            type="text"
                            name="postal_code"
                            disabled={isLocked("postal_code")}
                            value={form.postal_code}
                            onChange={handleChange}
                            className="mt-1 block min-h-[50px] w-full text-[#1111117A] border-[1px] border-[#11111126] rounded-[8px] shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                        />
                    </div>

                    {/* ICCID with barcode scanner */}
                    <div className="relative">
                        <label className="block text-[13px] font-medium text-gray-700">ICCID <span className="text-red-500">*</span></label>
                        <input
                            autoComplete="off"
                            type="text"
                            name="iccid"
                            value={form.iccid}
                            onChange={handleChange}
                            className="mt-1 block min-h-[50px] w-full text-[#1111117A] border-[1px] border-[#11111126] rounded-[8px] shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                        />
                        <div className="absolute top-9 right-3">
                            <CiBarcode
                                className="size-6 cursor-pointer text-gray-500 hover:text-gray-700"
                                onClick={handleOpenBarCodeModal}
                            />
                        </div>
                    </div>

                    {/* MSISDN */}
                    <div className="space-y-1">
                        <label className="block text-[13px] font-medium text-gray-700">MSISDN <span className="text-red-500">*</span></label>
                        <div className="relative">
                            <input
                                autoComplete="off"
                                name="msisdn"
                                value={form.msisdn}
                                onChange={handleChange}
                                onFocus={() => setIsMsisdnDropdownOpen(true)}
                                onBlur={() => setTimeout(() => setIsMsisdnDropdownOpen(false), 150)}
                                placeholder="Select Mobile Number"
                                className="font-poppins w-full p-3 pr-12 rounded-[8px] outline-none text-[#1111117A] bg-transparent border-[1px] border-[#11111126] focus:ring-blue-500 focus:border-blue-500  placeholder:text-base placeholder:font-normal text-accent-silver-900 placeholder:text-accent-silver-900"
                            />
                            <button
                                type="button"
                                onClick={() => getAvailableNos(true)}
                                disabled={numbersLoading}
                                title="Refresh numbers"
                                aria-label="Refresh available numbers"
                                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 disabled:cursor-not-allowed disabled:opacity-60"
                            >
                                <FiRefreshCw className={`size-5 ${numbersLoading ? "animate-spin" : ""}`} />
                            </button>
                            {isMsisdnDropdownOpen && (
                                <div className="absolute z-20  max-h-48 w-full overflow-y-auto rounded-[8px] border-[1px] border-[#11111126] bg-white shadow-sm">
                                    {filteredAvailableNos.length > 0 ? (
                                        filteredAvailableNos.map((number) => (
                                            <button
                                                type="button"
                                                key={number.MSISDN}
                                                onMouseDown={() => handleMsisdnSelect(number.MSISDN)}
                                                className="font-poppins block w-full px-3 py-2 text-left  text-[#1111117A] hover:bg-gray-50"
                                            >
                                                {number.MSISDN}
                                            </button>
                                        ))
                                    ) : (
                                        <p className="font-poppins px-3 py-2  text-[#1111117A]">
                                            No mobile number found
                                        </p>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>
                    {/* Submit Button */}
                    <div>
                        <button
                            type="submit"
                            disabled={loading}
                            className="mt-2 w-full bg-primary text-white py-4 rounded-full text-[12px] font-[400]"
                        >
                            {loading ? "Processing..." : "Submit"}
                        </button>
                    </div>
                </form>
            </div>

            {/* Error Modal */}
            {errorModal.open && (
                <ErrorModal
                    message={errorModal.message}
                    close={() => setErrorModal({ open: false, message: "" })}
                />
            )}

            {successModal.open && (
                <SuccessModal
                    message={successModal.message}
                    close={() => setSuccessModal({ open: false, message: "" })}
                />
            )}

            {/* Scanner Modal */}
            {openModal && <ScannerModal closeModal={closeModal} getCode={getCode} />}
        </>
    );
};

export default SelfRegister;
